<template>
  <aside class="tsp" role="dialog" aria-label="Task settings" data-test="task-settings-panel">
    <header class="tsp__head">
      <h2 class="tsp__title">Task settings</h2>
      <button type="button" class="tsp__close" title="Close" aria-label="Close" data-test="task-settings-close" @click="close">
        <ts-icon name="x" :size="18" />
      </button>
    </header>

    <div class="tsp__body">
      <section class="tsp__sec" data-test="task-settings-notifications">
        <div class="tsp__sec-head">
          <h3 class="tsp__sec-title">Your notifications</h3>
          <span class="tsp__badge">Only you</span>
        </div>

        <followups-switch
          v-model="on"
          class="tsp__switch"
          label="Play a sound and show a desktop notification when someone assigns me a task"
          data-test="task-settings-sound-push"
        />

        <div v-if="on" class="tsp__sound">
          <label class="tsp__label" for="tsp-sound">Sound</label>
          <div class="tsp__sound-row">
            <select id="tsp-sound" v-model="sound" class="tsp__select" data-test="task-settings-sound">
              <option v-for="s in sounds" :key="s.filename" :value="s.filename">{{ s.label }}</option>
            </select>
            <button type="button" class="tsp__btn" data-test="task-settings-play" @click="playSound">
              <ts-icon name="play" :size="14" :stroke="2" />{{ playing ? 'Playing…' : 'Play' }}
            </button>
          </div>
        </div>

        <div class="tsp__status" :class="`tsp__status--${status.tone}`" data-test="task-settings-status" :data-tone="status.tone">
          <button
            type="button"
            class="tsp__status-head"
            :aria-expanded="help ? 'true' : 'false'"
            data-test="task-settings-status-toggle"
            @click="help = !help"
          >
            <span class="tsp__tone"><ts-icon :name="status.tone === 'ok' ? 'checkCircle' : (status.tone === 'bad' ? 'xCircle' : 'alert')" :size="18" /></span>
            <span class="tsp__status-text">
              <span class="tsp__status-title" data-test="task-settings-status-title">{{ status.title }}</span>
              <span class="tsp__status-sub">{{ status.sub }}</span>
            </span>
            <span class="tsp__chev"><ts-icon :name="help ? 'chevUp' : 'chev'" :size="16" /></span>
          </button>

          <div v-if="help" class="tsp__status-body" data-test="task-settings-help">
            <div class="tsp__checks">
              <div v-for="c in checks" :key="c.key" class="tsp__check" data-test="task-settings-check" :data-check="c.key">
                <span class="tsp__check-ic" :class="`is-${c.tone}`"><ts-icon :name="c.tone === 'ok' ? 'checkCircle' : (c.tone === 'bad' ? 'xCircle' : 'alert')" :size="18" /></span>
                <span class="tsp__check-label">{{ c.label }}</span>
                <span class="tsp__check-val" data-test="task-settings-check-value">{{ c.value }}</span>
              </div>
            </div>

            <div class="tsp__tests">
              <button type="button" class="tsp__btn" data-test="task-settings-test-notification" @click="testNotification">
                <ts-icon name="monitor" :size="16" />Test notification
              </button>
              <button type="button" class="tsp__btn" data-test="task-settings-test-sound" @click="testSound">
                <ts-icon name="volume" :size="16" />Test sound
              </button>
            </div>

            <div>
              <div class="tsp__eyebrow">If something’s blocked</div>
              <div class="tsp__pills" role="group" aria-label="Your browser">
                <button
                  v-for="b in browsers"
                  :key="b.key"
                  type="button"
                  class="tsp__pill"
                  :class="{ 'is-on': b.key === browser }"
                  :aria-pressed="b.key === browser ? 'true' : 'false'"
                  data-test="task-settings-browser"
                  :data-browser="b.key"
                  @click="browser = b.key"
                >{{ b.label }}</button>
              </div>
              <ol class="tsp__steps" data-test="task-settings-steps">
                <li v-for="(t, i) in steps" :key="i">{{ t }}</li>
              </ol>
            </div>

            <div class="tsp__tips" data-test="task-settings-tips">
              <div class="tsp__tips-title">Still not working?</div>
              <ul class="tsp__tips-list">
                <li>Turn off Focus or Do Not Disturb on your computer.</li>
                <li>Check your computer’s notification settings and make sure your browser is allowed.</li>
                <li>Some browsers only show notifications when Teamio isn’t the tab you’re looking at. Switch tabs and test again.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section class="tsp__sec tsp__sec--perm" data-test="task-settings-permissions">
        <div class="tsp__sec-head">
          <h3 class="tsp__sec-title">Practice permissions</h3>
          <span class="tsp__badge tsp__badge--teal">Everyone at the practice</span>
        </div>
        <div id="tsp-who" class="tsp__q">Who can delete tasks?</div>
        <div class="tsp__radios" role="radiogroup" aria-labelledby="tsp-who">
          <label
            v-for="w in whoOptions"
            :key="w.value"
            class="tsp__radio"
            :class="{ 'is-on': who === w.value }"
            data-test="task-settings-who"
            :data-value="w.value"
          >
            <input v-model="who" type="radio" name="tspWhoCanDelete" class="tsp__radio-input" :value="w.value" />
            <span class="tsp__radio-dot" aria-hidden="true"></span>
            <span class="tsp__radio-text">
              <span class="tsp__radio-label">{{ w.label }}</span>
              <span class="tsp__radio-desc">{{ w.desc }}</span>
            </span>
          </label>
        </div>
      </section>
    </div>

    <div v-if="dirty" class="tsp__savebar" data-test="task-settings-savebar">
      <span class="tsp__savebar-text">Unsaved changes</span>
      <span class="tsp__spacer"></span>
      <button type="button" class="tsp__btn tsp__btn--ghost" data-test="task-settings-cancel" :disabled="saving" @click="cancel">Cancel</button>
      <button type="button" class="tsp__btn tsp__btn--primary" data-test="task-settings-save" :disabled="saving" @click="save">
        {{ saving ? 'Saving…' : 'Save' }}
      </button>
    </div>
  </aside>
</template>

<script>
/**
 * Task settings — the redesigned slide-in panel (design "Task Reports & Settings" 2a, 2026-10-07),
 * opened from the new Tasks page's gear (ToDo.vue tkOpenSettings → $showPanel, right, 440 wide).
 * Plan: C:\Users\nilay\.claude\plans\shiny-gliding-moth.md.
 *
 * Same settings, same storage as the classic TaskSettings.vue (which stays, untouched, for the
 * classic page):
 *   • Your notifications (per user) → src/utils/notificationPrefs.js setPref(userID, 'task',
 *     { soundPush, sound }) — localStorage, no network.
 *   • Who can delete tasks (site-wide) → TASKS_WHO_CAN_DELETE ('creator' default | 'anyone') with
 *     TaskSettings.vue's exact call: customer/saveSettings { settingsFor: 'TASKS', settings, siteID },
 *     then customer/getSettings. Only dispatched when that choice changed (a personal change needs no
 *     site-wide write). No role check — the classic panel has none either (flagged to Nilay).
 * The status box, the tests and the browser steps reuse the utils NotificationHelp.vue is built on
 * (notificationStatus.js, browserDetection.js, audioMuteDetection.js, push.js) — NotificationHelp.vue
 * itself is shared and stays as it is.
 *
 * Save bar only when something changed: Cancel puts the saved values back; Save writes, toasts
 * "Task settings saved" and keeps the panel open. The X, Esc and the backdrop close the panel
 * WITHOUT saving (emit 'closePanel' — vue2-slideout-panel binds only that event).
 */
import Push from 'push.js';
import FollowupsSwitch from '@/components/Followups/FollowupsSwitch.vue';
import { getPrefs, setPref } from '@/utils/notificationPrefs';
import { CURATED_NOTIFICATION_SOUNDS } from '@/utils/taskNotificationDetection';
import { readNotificationPermission, hasActionableProblem } from '@/utils/notificationStatus';
import { detectSiteAudioMuted } from '@/utils/audioMuteDetection';
import { detectBrowser, BROWSERS } from '@/utils/browserDetection';

const PLAY_MS = 1200;

// Lucide path data (ISC/MIT, as FuIcon.vue) on a 24×24 viewBox, stroked in currentColor.
const ICONS = {
  x: [['path', 'M18 6 6 18M6 6l12 12']],
  play: [['polygon', '6 3 20 12 6 21 6 3']],
  chev: [['path', 'm6 9 6 6 6-6']],
  chevUp: [['path', 'm18 15-6-6-6 6']],
  checkCircle: [['circle', '12 12 10'], ['path', 'm9 12 2 2 4-4']],
  xCircle: [['circle', '12 12 10'], ['path', 'm15 9-6 6M9 9l6 6']],
  alert: [['circle', '12 12 10'], ['path', 'M12 8v4M12 16h.01']],
  monitor: [['rect', '2 3 20 14 2'], ['path', 'M8 21h8M12 17v4']],
  volume: [['path', 'M11 5 6 9H2v6h4l5 4V5z'], ['path', 'M15.54 8.46a5 5 0 0 1 0 7.07']],
};

function shape(h, kind, v) {
  if (kind === 'circle') {
    const [cx, cy, r] = v.split(' ');
    return h('circle', { attrs: { cx, cy, r } });
  }
  if (kind === 'rect') {
    const [x, y, width, height, rx] = v.split(' ');
    return h('rect', { attrs: { x, y, width, height, rx } });
  }
  if (kind === 'polygon') return h('polygon', { attrs: { points: v } });
  return h('path', { attrs: { d: v } });
}

const TsIcon = {
  name: 'TsIcon',
  functional: true,
  props: {
    name: { type: String, required: true },
    size: { type: [Number, String], default: 16 },
    stroke: { type: [Number, String], default: 1.9 },
  },
  render(h, { props }) {
    return h('svg', {
      staticClass: 'tsp__icon',
      attrs: {
        width: props.size, height: props.size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor',
        'stroke-width': props.stroke, 'stroke-linecap': 'round', 'stroke-linejoin': 'round',
        'aria-hidden': 'true', focusable: 'false',
      },
    }, (ICONS[props.name] || []).map(([kind, v]) => shape(h, kind, v)));
  },
};

/** The design's per-browser steps ("If something’s blocked"). */
const STEPS = {
  chrome: ['Click the site-info icon at the left end of the address bar.', 'Set Notifications to Allow.', 'Set Sound to Allow.', 'Reload the page.'],
  edge: ['Click the lock icon at the left end of the address bar.', 'Open Permissions for this site.', 'Set Notifications and Sound to Allow.', 'Reload the page.'],
  firefox: ['Click the lock icon at the left end of the address bar.', 'Choose Connection secure, then More information, then the Permissions tab.',
    'Untick Use default next to Send notifications and pick Allow.', 'Make sure the tab isn’t muted (right-click the tab, then Unmute tab), then reload.'],
  safari: ['Open Safari, then Settings, then the Websites tab.', 'Choose Notifications and set this site to Allow.',
    'Choose Auto-Play and set this site to Allow all auto-play.', 'Reload the page.'],
};

const WHO_OPTIONS = [
  { value: 'creator', label: 'Only the person who created it', desc: 'People assigned to a task can complete it, but can’t delete it.' },
  { value: 'anyone', label: 'Anyone at the practice', desc: 'Any teammate can delete any task.' },
];

const PERMISSION_ROWS = {
  granted: { tone: 'ok', value: 'Allowed in this browser' },
  denied: { tone: 'bad', value: 'Blocked' },
  default: { tone: 'warn', value: 'Not asked yet' },
  unsupported: { tone: 'warn', value: 'Not supported by this browser' },
};

const SOUND_ROWS = {
  audible: { tone: 'ok', value: 'Working' },
  muted: { tone: 'bad', value: 'Muted for this site' },
  unknown: { tone: 'warn', value: 'Not tested yet' },
};

export default {
  name: 'TaskSettingsPanel',
  components: { FollowupsSwitch, TsIcon },
  data() {
    return {
      on: true,
      sound: 'bell-123742.mp3',
      who: 'creator',
      saved: { on: true, sound: 'bell-123742.mp3', who: 'creator' },
      help: false,
      permission: 'unsupported',
      soundStatus: 'unknown',
      browser: 'chrome',
      playing: false,
      playTimer: null,
      saving: false,
    };
  },
  computed: {
    userID() {
      return this.$store.state.customer.userID;
    },
    siteID() {
      return this.$store.state.customer.siteID;
    },
    /** TASKS_WHO_CAN_DELETE off the settings rows (as TaskSettings.vue reads it), 'creator' when absent. */
    whoFromSettings() {
      const rows = this.$store.state.customer.settings;
      if (!Array.isArray(rows)) return 'creator';
      const row = rows.find(s => s && s.Property === 'TASKS_WHO_CAN_DELETE');
      return row && row.PropertyValue === 'anyone' ? 'anyone' : 'creator';
    },
    sounds() {
      return CURATED_NOTIFICATION_SOUNDS;
    },
    browsers() {
      return ['chrome', 'edge', 'firefox', 'safari'].map(k => BROWSERS[k]);
    },
    steps() {
      return STEPS[this.browser] || STEPS.chrome;
    },
    whoOptions() {
      return WHO_OPTIONS;
    },
    dirty() {
      const s = this.saved;
      return this.on !== s.on || this.sound !== s.sound || this.who !== s.who;
    },
    /** The status box's one-line summary: off / blocked / muted / not asked / unsupported / working / untested. */
    status() {
      if (!this.on) {
        return { tone: 'off', title: 'Notifications are off', sub: 'Turn them on above to get a sound and a desktop alert for new tasks.' };
      }
      const p = this.permission;
      const s = this.soundStatus;
      if (p === 'denied') {
        return { tone: 'bad', title: 'Desktop notifications are blocked', sub: 'Your browser is blocking them for Teamio. The steps below show how to allow them.' };
      }
      if (s === 'muted') {
        return { tone: 'bad', title: 'Sound is muted for this site', sub: 'Your browser is muting Teamio. The steps below show how to turn sound on.' };
      }
      if (p === 'default') {
        return { tone: 'warn', title: 'Desktop notifications aren’t on yet', sub: 'Click Test notification and choose Allow when your browser asks.' };
      }
      if (p === 'unsupported') {
        return { tone: 'warn', title: 'This browser can’t show desktop notifications', sub: 'You’ll still hear the sound while Teamio is open.' };
      }
      if (s === 'audible') {
        return { tone: 'ok', title: 'Notifications and sound are working', sub: 'Tested on this computer just now.' };
      }
      return { tone: 'warn', title: 'Notifications look good. Run a sound test to be sure.', sub: 'Your browser allows notifications. Sound hasn’t been checked yet.' };
    },
    checks() {
      const p = PERMISSION_ROWS[this.permission] || PERMISSION_ROWS.unsupported;
      const s = SOUND_ROWS[this.soundStatus] || SOUND_ROWS.unknown;
      return [
        { key: 'desktop', label: 'Desktop notifications', tone: p.tone, value: p.value },
        { key: 'sound', label: 'Sound for this site', tone: s.tone, value: s.value },
      ];
    },
  },
  created() {
    const prefs = getPrefs(this.userID).task;
    this.on = prefs.soundPush;
    this.sound = prefs.sound;
    this.who = this.whoFromSettings;
    this.saved = { on: this.on, sound: this.sound, who: this.who };
    this.refreshPermission();
    const ua = typeof navigator !== 'undefined' && navigator.userAgent ? navigator.userAgent : '';
    const b = detectBrowser(ua);
    this.browser = b === 'other' ? 'chrome' : b;
    this.help = this.on && hasActionableProblem(this.permission, this.soundStatus);
  },
  beforeDestroy() {
    if (this.playTimer) clearTimeout(this.playTimer);
  },
  methods: {
    refreshPermission() {
      this.permission = readNotificationPermission(typeof window !== 'undefined' ? window.Notification : undefined);
    },

    /** Plays a sound file from /sounds; "Playing…" for a moment. Autoplay refusals are ignored. */
    play(file) {
      try {
        const audio = new Audio(String(file).charAt(0) === '/' ? file : `/sounds/${file}`);
        const p = audio.play();
        if (p && typeof p.catch === 'function') p.catch(() => { /* autoplay blocked */ });
      } catch (e) { /* no audio here */ }
      this.playing = true;
      if (this.playTimer) clearTimeout(this.playTimer);
      this.playTimer = setTimeout(() => {
        this.playing = false;
        this.playTimer = null;
      }, PLAY_MS);
    },

    playSound() {
      this.play(this.sound);
    },

    async testSound() {
      this.play(this.sound);
      try {
        this.soundStatus = await detectSiteAudioMuted();
      } catch (e) {
        this.soundStatus = 'unknown';
      }
    },

    /** As NotificationHelp.vue: ask first when never asked; warn when blocked; else show a test one. */
    testNotification() {
      this.refreshPermission();
      if (this.permission === 'default') {
        try {
          Push.Permission.request(
            () => { this.permission = 'granted'; this.firePush(); },
            () => { this.refreshPermission(); },
          );
          return;
        } catch (e) { /* fall through */ }
      }
      if (this.permission === 'denied' || this.permission === 'unsupported') {
        this.help = true;
        if (this.$toast && typeof this.$toast.warning === 'function') {
          this.$toast.warning('Notifications are blocked. Follow the steps below to allow them.');
        }
        return;
      }
      this.firePush();
    },

    firePush() {
      try {
        Push.create('Teamio test notification', {
          body: 'If you can see this, desktop notifications are working.',
          timeout: 6000,
        });
      } catch (e) { /* ignore */ }
      this.refreshPermission();
    },

    cancel() {
      this.on = this.saved.on;
      this.sound = this.saved.sound;
      this.who = this.saved.who;
    },

    async save() {
      if (this.saving || !this.dirty) return;
      this.saving = true;
      try {
        // Per-user notification prefs → localStorage (no network).
        setPref(this.userID, 'task', { soundPush: this.on, sound: this.sound });

        // Site-wide policy → DB, exactly as TaskSettings.vue — only when it changed.
        if (this.who !== this.saved.who) {
          this.$store.dispatch('alert/setErrored', false);
          await this.$store.dispatch('customer/saveSettings', {
            settingsFor: 'TASKS',
            settings: { TASKS_WHO_CAN_DELETE: this.who },
            siteID: this.siteID,
          });
          if (this.$store.state.alert && this.$store.state.alert.errored) {
            this.saved = Object.assign({}, this.saved, { on: this.on, sound: this.sound });
            if (this.$toast) this.$toast.error('Failed to save who can delete tasks. Please try again.');
            return;
          }
          await this.$store.dispatch('customer/getSettings', { siteID: this.siteID });
        }
        this.saved = { on: this.on, sound: this.sound, who: this.who };
        if (this.$toast && typeof this.$toast.success === 'function') this.$toast.success('Task settings saved');
      } catch (e) {
        console.error('[Task settings] save failed:', e);
        if (this.$toast && typeof this.$toast.error === 'function') this.$toast.error('Failed to save task settings. Please try again.');
      } finally {
        this.saving = false;
      }
    },

    close() {
      this.$emit('closePanel', {});
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../../components/Followups/mixins';

.tsp {
  @include fu-font-base;
  display: flex;
  flex-direction: column;
  // Exactly the slide-out's height (vue2-slideout-panel `.slideout` is fixed, top 0 / bottom 0) with the
  // BODY as the scroller, so the save bar stays pinned at the bottom (as TaskDrawer.vue does).
  height: 100%;
  min-height: 0;
  background: $fu-surface;
}

.tsp__head {
  flex: none;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 18px 16px 16px 24px;
  border-bottom: 1px solid $fu-border;
}

.tsp__title {
  @include fu-font-display;
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: 22px;
  font-weight: 800;
  color: $fu-text;
}

.tsp__close { @include fu-icon-btn(36px); }

.tsp__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.tsp__sec {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px 24px;
}

.tsp__sec--perm {
  gap: 12px;
  padding-bottom: 24px;
  border-top: 1px solid $fu-border;
}

.tsp__sec-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.tsp__sec-title {
  margin: 0;
  font-size: 15px;
  font-weight: 800;
  color: $fu-text;
}

.tsp__badge {
  @include fu-pill($fu-neutral-100, $fu-neutral-600);
  border: 1px solid $fu-border;
}

.tsp__badge--teal {
  background: $fu-brand-50;
  color: $fu-brand-700;
  border-color: $fu-brand-100;
}

.tsp__switch {
  align-items: flex-start;
  ::v-deep .fu-switch__track { margin-top: 1px; }
  ::v-deep .fu-switch__label { font-size: $fu-fs-body-sm; font-weight: $fu-fw-medium; line-height: 1.4; }
}

.tsp__label {
  display: block;
  margin: 0 0 6px;
  font-size: 13px;
  font-weight: $fu-fw-bold;
  color: $fu-text;
}

.tsp__sound-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.tsp__select {
  @include fu-input(40px);
  flex: 1 1 auto;
  min-width: 0;
  padding-right: 30px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23717977' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  cursor: pointer;
}

.tsp__btn {
  @include fu-btn(secondary, sm);
  flex: none;
}

.tsp__btn--ghost { @include fu-btn(ghost, sm); }
.tsp__btn--primary { @include fu-btn(primary, sm); }

// ---- Status box --------------------------------------------------------------------------------

.tsp__status {
  border: 1px solid $fu-border;
  border-radius: 12px;
  overflow: hidden;
}

.tsp__status-head {
  @include fu-button-reset;
  @include fu-focus-ring;
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 14px;
  user-select: none;
  &:hover { background: $fu-neutral-25; }
}

.tsp__tone {
  display: flex;
  flex: none;
}

.tsp__status--ok .tsp__tone { color: $fu-success; }
.tsp__status--warn .tsp__tone { color: $fu-warning; }
.tsp__status--bad .tsp__tone { color: $fu-danger; }
.tsp__status--off .tsp__tone { color: $fu-neutral-400; }

.tsp__status-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.tsp__status-title {
  font-size: 13.5px;
  font-weight: $fu-fw-bold;
  line-height: 1.35;
  color: $fu-text;
}

.tsp__status-sub {
  font-size: 12.5px;
  line-height: 1.4;
  color: $fu-text-muted;
}

.tsp__chev {
  display: flex;
  flex: none;
  color: $fu-neutral-400;
}

.tsp__status-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 4px 14px 16px;
}

.tsp__checks {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.tsp__check {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  background: $fu-neutral-25;
  border: 1px solid $fu-border;
  min-width: 0;
}

.tsp__check-ic {
  display: flex;
  flex: none;
  &.is-ok { color: $fu-success; }
  &.is-warn { color: $fu-warning; }
  &.is-bad { color: $fu-danger; }
}

.tsp__check-label {
  flex: 1;
  min-width: 0;
  font-size: 13.5px;
  font-weight: $fu-fw-bold;
}

.tsp__check-val {
  font-size: 12.5px;
  color: $fu-neutral-600;
  text-align: right;
}

.tsp__tests {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tsp__eyebrow {
  @include fu-eyebrow($fu-text-muted);
  margin-bottom: 8px;
}

.tsp__pills {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 2px;
  padding: 3px;
  max-width: 100%;
  border-radius: $fu-radius-md;
  background: $fu-neutral-100;
}

.tsp__pill {
  @include fu-button-reset;
  @include fu-focus-ring;
  padding: 6px 14px;
  border-radius: $fu-radius-sm;
  font-size: 13px;
  font-weight: $fu-fw-semibold;
  color: $fu-neutral-600;
  &:hover { color: $fu-text; }
  &.is-on {
    background: $fu-surface;
    color: $fu-text;
    box-shadow: $fu-shadow-xs;
  }
}

// Block lists (not flex): flex items lose their list markers, and the steps need their numbers.
.tsp__steps {
  margin: 10px 0 0;
  padding-left: 20px;
  list-style: decimal outside;
  font-size: 13.5px;
  line-height: 1.55;
  color: $fu-neutral-700;
  li + li { margin-top: 3px; }
}

.tsp__tips-title {
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: $fu-fw-bold;
}

.tsp__tips-list {
  margin: 0;
  padding-left: 20px;
  list-style: disc outside;
  li + li { margin-top: 3px; }
  font-size: 13.5px;
  line-height: 1.55;
  color: $fu-neutral-600;
}

// ---- Who can delete ----------------------------------------------------------------------------

.tsp__q {
  font-size: 13.5px;
  font-weight: $fu-fw-bold;
}

.tsp__radios {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.tsp__radio {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 0;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1.5px solid $fu-border;
  background: $fu-surface;
  cursor: pointer;
  transition: border-color $fu-dur-fast $fu-ease-out, background $fu-dur-fast $fu-ease-out;
  &:hover { border-color: $fu-border-strong; }
  &.is-on {
    border-color: $fu-brand;
    background: $fu-brand-50;
  }
}

.tsp__radio-input { @include fu-visually-hidden; }

.tsp__radio-dot {
  position: relative;
  flex: none;
  width: 20px;
  height: 20px;
  margin-top: 1px;
  border-radius: 50%;
  border: 1.5px solid $fu-border-strong;
  background: $fu-surface;
  &::after {
    content: '';
    position: absolute;
    top: 4px;
    left: 4px;
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: $fu-brand;
    transform: scale(0);
    transition: transform $fu-dur-fast $fu-ease-out;
  }
}

.tsp__radio-input:checked + .tsp__radio-dot {
  border-color: $fu-brand;
  &::after { transform: scale(1); }
}

.tsp__radio-input:focus-visible + .tsp__radio-dot { box-shadow: $fu-ring; }

.tsp__radio-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.tsp__radio-label {
  font-size: $fu-fs-body-sm;
  font-weight: $fu-fw-semibold;
  color: $fu-text;
}

.tsp__radio-desc {
  margin-top: 2px;
  font-size: $fu-fs-caption;
  color: $fu-text-muted;
}

// ---- Save bar ----------------------------------------------------------------------------------

.tsp__savebar {
  flex: none;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 24px;
  border-top: 1px solid $fu-border;
  background: $fu-surface;
}

.tsp__savebar-text {
  font-size: 13.5px;
  font-weight: $fu-fw-semibold;
}

.tsp__spacer { margin-left: auto; }

@include fu-phone {
  .tsp__head { padding: 14px 12px 12px 16px; }
  .tsp__sec { padding: 16px; }
  .tsp__savebar { padding: 12px 16px; }
}
</style>

<style lang="scss">
// The design's lighter backdrop (rgba(12,15,14,.18)) behind THIS panel only. vue2-slideout-panel
// draws its own rgba(0,0,0,.5) layer; ToDo.vue opens this panel with cssClass 'tsp-slideout'.
// Browsers without :has() keep the library's darker one.
.slideout-panel:has(.tsp-slideout) .slideout-panel-bg {
  background-color: rgba(12, 15, 14, 0.18);
}
</style>
